import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";

import { defaultGenericMeta, GenericStateMeta } from "store/common";
import { fetchConfig } from "./config-action";
import {
  CombinedConfiguration,
  Configuration,
  createDefaultConfig,
} from "./configuration";

export interface ConfigurationState extends Configuration, GenericStateMeta {}

/**
 * 36681: If state.paymentCenter.applicationName exists, use that for the
 *        applicationName.
 *        If state.client.name exists but state.paymentCenter.applicationName
 *        does not, use that for the applicationName.
 */
function getApplicationName(state: ConfigurationState): string {
  let applicationName = "";

  if (state.paymentCenter.applicationName) {
    applicationName = state.paymentCenter.applicationName;
  } else if (state.client?.name && !state.paymentCenter?.applicationName) {
    applicationName = state.client.name;
  }

  return applicationName;
}

export const configSlice = createSlice({
  name: "config",
  initialState: {
    ...defaultGenericMeta,
    ...createDefaultConfig(null, null),
  } as ConfigurationState,
  reducers: {
    updateConfig(state, action: PayloadAction<Configuration>) {
      state.client = action.payload.client;
      state.paymentCenter = action.payload.paymentCenter;
    },
    resetConfig(state) {
      const defaultConfig = createDefaultConfig(null, null);
      state.client = defaultConfig.client;
      state.paymentCenter = defaultConfig.paymentCenter;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchConfig.fulfilled,
      (state, action: PayloadAction<CombinedConfiguration>) => {
        state.client = action.payload.client;
        state.ewallet = action.payload.ewallet;
        state.notifications = action.payload.notifications;
        state.paymentCenter = action.payload.paymentCenter;
        state.scheduledPayments = action.payload.scheduledPayments;
        state.isLoading = false;
        state.error = false;
        state.applicationName = getApplicationName(state);
      }
    );
    builder.addCase(fetchConfig.rejected, (state, action) => {
      state.isLoading = false;
      if (!!action.payload?.statusMessage) {
        state.error = action.payload;
      } else {
        state.error = true;
      }
    });
    builder.addCase(fetchConfig.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(PURGE, () => {
      return {
        ...defaultGenericMeta,
        ...createDefaultConfig(null, null),
      } as ConfigurationState;
    });
  },
});

// export const selectConfig = (state: RootState) => state.config;

export const { updateConfig, resetConfig } = configSlice.actions;

export default configSlice.reducer;
